import React, { FC } from 'react';
import { Box, Card, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import 'flag-icons/css/flag-icons.min.css';
import { ALL_COUNTRIES } from '@/data/countries';
import { countriesAddedByPlan, countriesForPlan, displayName, sortByDisplayName } from '@/data/plans';

const flagCode = (iso3: string): string | undefined => ALL_COUNTRIES.find((c) => c.code === iso3)?.flagCode;

interface PlanColumnProps {
  name: string;
  caption: string;
  countries: string[];
  highlighted?: boolean;
}

const PlanColumn: FC<PlanColumnProps> = ({ name, caption, countries, highlighted }) => (
  <Card.Root
    variant={highlighted ? 'elevated' : 'outline'}
    borderWidth={highlighted ? '2px' : '1px'}
    borderColor={highlighted ? 'brand.400' : undefined}
    h='full'
  >
    <Card.Body>
      <Text fontSize='sm' fontWeight='bold' textTransform='uppercase' letterSpacing='wide' color='brand.500'>
        {name}
      </Text>
      <Text fontSize='sm' color='fg.muted' mt='1' mb='4'>
        {caption}
      </Text>
      <Flex wrap='wrap' gap='2'>
        {sortByDisplayName(countries).map((iso3) => (
          <Flex
            key={iso3}
            as='span'
            alignItems='center'
            gap='2'
            px='2.5'
            py='1'
            borderRadius='full'
            borderWidth='1px'
            borderColor='border.muted'
            fontSize='sm'
            lineHeight='1.2'
          >
            {flagCode(iso3) && <Box as='span' className={`fi fi-${flagCode(iso3)}`} borderRadius='sm' flexShrink={0} />}
            <Text as='span'>{displayName(iso3)}</Text>
          </Flex>
        ))}
      </Flex>
    </Card.Body>
  </Card.Root>
);

const CountriesByPlan: FC = () => {
  const { t } = useTranslation('landing');
  return (
    <Box id='countries-by-plan' mt='16'>
      <Heading size='lg' mb='2' textAlign='center'>
        {t('homepage.pricing.countriesTitle')}
      </Heading>
      <Text fontSize='sm' color='fg.muted' textAlign='center' mb='8'>
        {t('homepage.pricing.countriesSubtitle')}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap='6' alignItems='stretch'>
        <PlanColumn name='Free & Lite' caption={t('homepage.pricing.freeLiteIncludes')} countries={countriesForPlan('Lite')} />
        <PlanColumn
          name='Plus'
          caption={t('homepage.pricing.plusAddsAll')}
          countries={countriesAddedByPlan('Plus')}
          highlighted
        />
        <PlanColumn
          name='Enterprise'
          caption={t('homepage.pricing.enterpriseAddsAll')}
          countries={countriesAddedByPlan('Enterprise')}
        />
      </SimpleGrid>
    </Box>
  );
};

export default CountriesByPlan;
